import { useEffect, useState } from "react";
import { Hotel, Dice5, Store, Landmark, Play, Square, History, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorText } from "@/lib/api";
import { HourlyChart } from "@/components/doorman/HourlyChart";

const ICONS = { hotel: Hotel, casino: Dice5, vendor: Store, bank: Landmark };
const SPEEDS = [["1", "Real time (x1)"], ["10", "x10"], ["60", "x60 — an hour per minute"], ["360", "x360 — a day in 4 min"]];

export const SimulatorPanel = ({ tick, onChange, onViewSandbox, sandbox }) => {
  const [profiles, setProfiles] = useState([]);
  const [profile, setProfile] = useState("hotel");
  const [days, setDays] = useState("1");
  const [speed, setSpeed] = useState("60");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/sim/profiles").then((r) => setProfiles(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/sim/status").then((r) => setStatus(r.data)).catch(() => {}); }, [tick]);
  useEffect(() => {
    if (!status?.running) return;
    const id = setInterval(onChange, 3000);
    return () => clearInterval(id);
  }, [status?.running, onChange]);

  const act = async (name, fn, msg) => {
    setBusy(name);
    try {
      const { data } = await fn();
      toast.success(typeof msg === "function" ? msg(data) : msg);
      if (name !== "stop" && name !== "reset") onViewSandbox();
      onChange();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy("");
    }
  };

  const p = profiles.find((x) => x.id === profile);
  const runningLabel = status?.running && profiles.find((x) => x.id === status.profile)?.label;

  return (
    <section className="space-y-6">
      <p className="text-sm text-slate-400 max-w-3xl">
        Play typical AI agent traffic for a business type into your <span className="text-amber-300">Simulation ledger</span>. It works exactly like the live ledger (hash chain, verify, Bitcoin anchors) but never touches your real records.
      </p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {profiles.map((x) => <ProfileCard key={x.id} p={x} active={profile === x.id} onClick={() => setProfile(x.id)} />)}
      </div>

      {p && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="rounded-md border border-[#222D3D] bg-[#141A24] p-5 space-y-4">
            <div className="flex items-center gap-2 text-slate-200"><History className="h-4 w-4 text-amber-400" /><span className="font-display font-semibold">Fill history</span></div>
            <p className="text-xs text-slate-500">Replaces the simulation ledger with {p.label.toLowerCase()} traffic for the past days, ending now.</p>
            <div className="flex gap-3">
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-32 bg-[#07090E]" data-testid="sim-days-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["1", "3", "7"].map((d) => <SelectItem key={d} value={d} data-testid={`sim-days-${d}`}>{d} day{d !== "1" && "s"}</SelectItem>)}</SelectContent>
              </Select>
              <Button className="flex-1 bg-amber-500 hover:bg-amber-400 text-[#07090E] font-semibold" disabled={!!busy} data-testid="sim-backfill-btn"
                onClick={() => act("backfill", () => api.post("/sim/backfill", { profile, days: Number(days) }), (d) => `${d.created.toLocaleString()} arrivals generated`)}>
                {busy === "backfill" ? <Loader2 className="h-4 w-4 animate-spin" /> : `Generate ~${(p.daily * Number(days)).toLocaleString()} arrivals`}
              </Button>
            </div>
          </div>

          <div className="rounded-md border border-[#222D3D] bg-[#141A24] p-5 space-y-4">
            <div className="flex items-center gap-2 text-slate-200"><Play className="h-4 w-4 text-emerald-400" /><span className="font-display font-semibold">Live stream</span></div>
            <p className="text-xs text-slate-500">Adds arrivals as they happen, at the typical rate or sped up so you can watch volume build.</p>
            <div className="flex gap-3">
              <Select value={speed} onValueChange={setSpeed}>
                <SelectTrigger className="w-56 bg-[#07090E]" data-testid="sim-speed-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SPEEDS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`sim-speed-${v}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
              {status?.running ? (
                <Button variant="outline" className="flex-1 border-rose-500/40 text-rose-300 bg-transparent" disabled={!!busy} data-testid="sim-stop-btn"
                  onClick={() => act("stop", () => api.post("/sim/stop"), "Simulation stopped")}>
                  <Square className="h-4 w-4 mr-2" />Stop
                </Button>
              ) : (
                <Button className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" disabled={!!busy} data-testid="sim-start-btn"
                  onClick={() => act("live", () => api.post("/sim/live", { profile, speed: Number(speed) }), "Live simulation started")}>
                  <Play className="h-4 w-4 mr-2" />Start
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#222D3D] bg-[#0F141C] px-4 py-3" data-testid="sim-status">
        <span className="text-sm text-slate-300 flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${status?.running ? "bg-emerald-400 pulse-dot" : "bg-slate-600"}`} />
          {status?.running ? `Streaming ${runningLabel} at x${status.speed} · ${status.generated} added this run` : "Not streaming"}
          <span className="text-slate-500">· {status?.sandbox_entries?.toLocaleString() ?? 0} entries in simulation ledger</span>
        </span>
        <div className="flex gap-2">
          {!sandbox && <Button size="sm" variant="outline" className="border-amber-500/40 text-amber-300 bg-transparent" onClick={onViewSandbox} data-testid="sim-view-btn">View simulation ledger</Button>}
          <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" disabled={!!busy} data-testid="sim-reset-btn"
            onClick={() => act("reset", () => api.post("/sim/reset"), "Simulation ledger cleared")}>
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />Clear simulation
          </Button>
        </div>
      </div>

      <HourlyChart tick={tick} />
    </section>
  );
};

const ProfileCard = ({ p, active, onClick }) => {
  const Icon = ICONS[p.id];
  return (
    <button onClick={onClick} data-testid={`sim-profile-${p.id}`}
      className={`text-left rounded-md border p-5 space-y-3 transition-colors ${active ? "border-amber-500/60 bg-amber-500/5" : "border-[#222D3D] bg-[#141A24] hover:border-slate-600"}`}>
      <Icon className={`h-5 w-5 ${active ? "text-amber-400" : "text-slate-400"}`} />
      <div>
        <p className="text-slate-100 font-medium">{p.label}</p>
        <p className="font-mono text-xs text-amber-300/90 mt-0.5">~{p.daily.toLocaleString()} arrivals / day</p>
        <p className="text-[11px] text-slate-500">{p.net_daily?.toLocaleString()} on the network · {p.web_daily?.toLocaleString()} on the website</p>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed">{p.summary}</p>
      <p className="text-[11px] text-slate-600 font-mono">Areas: {p.areas?.join(", ")}</p>
      <p className="text-[11px] text-slate-600 font-mono">{p.devices.length} device{p.devices.length > 1 && "s"}: {p.devices.join(", ")}</p>
    </button>
  );
};
